import React from 'react'
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Typography,
} from '@mui/material'
import _ from 'es-toolkit/compat'
import { useTranslation } from 'react-i18next'

import ConfirmDialog, { useConfirmDialog } from '@core/components/dialogs/dialog-confirm'
import Dropdown from '@core/components/dropdown/dropdown'

import { FormValues } from '../main-types'
import { categoryOptions } from './admin-add-customer'
import { useCustomer, useUpdateCustomer } from './admin-api'
import { AdminSettings } from './admin-settings-state'
import { FeatureFlagsSection } from './feature-flags-section'
import {
  defaultFormValues,
  handleFeatureFlagsChange,
  useFeatureFlagsProps,
} from './use-feature-flags-props'

export const AdminManageCustomer = ({ adminSettings }: { adminSettings: AdminSettings }) => {
  const { handleClose } = adminSettings
  const { t } = useTranslation('legacy', { keyPrefix: 'admin.manage_customer' })
  const updateCustomer = useUpdateCustomer()
  const { data: bffCustomer } = useCustomer()

  const { customer } = bffCustomer!
  const confirmArchive = useConfirmDialog()

  const values = React.useMemo<FormValues>(() => {
    const result = _.merge({}, defaultFormValues, _.cloneDeep(customer))
    result.features.export_file = _.uniq(result.features.export_file ?? [])
    return result
  }, [customer])

  const handleChange = React.useCallback(
    (field: string, value: boolean | string | undefined) => {
      const updated = handleFeatureFlagsChange(values, field, value)
      updateCustomer.mutate({
        features: updated.features,
        hasOptionRationale: updated.hasOptionRationale,
      })
    },
    [values, updateCustomer],
  )

  const { getCheckboxProps, getFilenameConventionProps } = useFeatureFlagsProps({
    values,
    disabled: updateCustomer.isPending,
    onChange: handleChange,
  })

  return (
    <Box>
      <DialogContent sx={{ pt: '0px !important' }}>
        <Box typography="caption" fontWeight="bold">
          {t('id')}
        </Box>
        <Box>{customer.id}</Box>

        <Box typography="caption" fontWeight="bold" mt={1}>
          {t('customer_name')}
        </Box>
        <Box>{customer.name}</Box>

        <Box display="flex" justifyContent="space-between">
          <Box>
            <Box typography="caption" fontWeight="bold" mt={1}>
              {t('created_at')}
            </Box>
            <Box>{new Date(customer.createdAt!).toLocaleString()}</Box>
          </Box>

          <Box>
            <Box typography="caption" fontWeight="bold" mt={1}>
              {t('updated_at')}
            </Box>
            <Box>{new Date(customer.updatedAt!).toLocaleString()}</Box>
          </Box>
        </Box>
        <Box typography="caption" fontWeight="bold" mt={1}>
          {t('sso_connection_name')}
        </Box>
        <Box>{customer.ssoConnectionName || t('none')}</Box>

        <Box mt={1}>
          <Typography variant="subtitle2" gutterBottom>
            {t('category')}
          </Typography>
          <Dropdown
            value={
              categoryOptions.find((o) => o.value === (customer.category || 'regular')) ?? null
            }
            onChange={(option) => updateCustomer.mutate({ category: option.value })}
            options={categoryOptions}
            disabled={updateCustomer.isPending}
          />
        </Box>
        <FormControlLabel
          disabled={updateCustomer.isPending}
          slotProps={{ typography: { fontSize: 14 } }}
          sx={{ display: 'flex' }}
          control={<Checkbox size="small" color="error" />}
          checked={customer.isArchived ?? false}
          label={t('archived')}
          onChange={() => {
            if (customer.isArchived) {
              updateCustomer.mutate({ isArchived: false })
            } else {
              confirmArchive.open(() => updateCustomer.mutateAsync({ isArchived: true }))
            }
          }}
        />
        <Box typography="caption" color="error.main">
          {t('archived_description')}
        </Box>

        <FeatureFlagsSection
          getCheckboxProps={getCheckboxProps}
          getFilenameConventionProps={getFilenameConventionProps}
        />

        <ConfirmDialog
          state={confirmArchive}
          id="confirm-archive-customer"
          title={t('archive_title')}
          confirmText={t('archive_confirm')}
          content={
            <Typography>
              {t('archive_warning_prefix')} <strong>{customer.name}</strong>{' '}
              {t('archive_warning_suffix')}
            </Typography>
          }
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={updateCustomer.isPending} variant="contained">
          {t('done')}
        </Button>
      </DialogActions>
    </Box>
  )
}
